import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { roleAllowed } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Search, PackageSearch, Truck, Factory, Boxes, ArrowLeft, ClipboardList,
  FileSpreadsheet, Upload, Download, Loader2, Columns3, PackageMinus,
} from "lucide-react";

const TO_STATUS_LABEL = {
  draft:         "Draft",
  order_raised:  "Order Raised",
  confirmed:     "Confirmed",
  in_production: "In Production",
  ready:         "Ready",
  delivered:     "Delivered",
  cancelled:     "Cancelled",
  revised:       "Revised",
};
const TO_STATUS_TONE = {
  draft:         "bg-slate-100 text-slate-700",
  order_raised:  "bg-amber-50 text-amber-800",
  confirmed:     "bg-blue-50 text-blue-800",
  in_production: "bg-purple-50 text-purple-800",
  ready:         "bg-teal-50 text-teal-800",
  delivered:     "bg-emerald-50 text-emerald-800",
  cancelled:     "bg-rose-50 text-rose-800",
  revised:       "bg-slate-100 text-slate-700",
};

// Same "SO-" stored code -> "TO-" printed number swap the backend does
// server-side (see _order_display_no) — used here for the list, before we
// even call the backend for a specific order's full status.
function toDisplayCode(code) {
  return code && code.startsWith("SO-") ? "TO-" + code.slice(3) : code || "";
}

const STAGE_TONE = {
  machine_shop:     "bg-amber-50 text-amber-800",
  impregnation:     "bg-teal-50 text-teal-800",
  fabrication:      "bg-cyan-50 text-cyan-800",
  powder_coating:   "bg-purple-50 text-purple-800",
  semi_finished:    "bg-indigo-50 text-indigo-800",
  assembly:         "bg-sky-50 text-sky-800",
  testing:          "bg-fuchsia-50 text-fuchsia-800",
  final_inspection: "bg-orange-50 text-orange-800",
  finished:         "bg-emerald-50 text-emerald-800",
  rework:           "bg-rose-50 text-rose-800",
};

const OVERALL_TONE = {
  "Not Started":                         "bg-slate-100 text-slate-700",
  "Work Order Raised — Not Started":     "bg-slate-100 text-slate-700",
  "In Production":                       "bg-amber-50 text-amber-800",
  "Ready for Dispatch":                  "bg-blue-50 text-blue-800",
  "Partially Delivered":                 "bg-teal-50 text-teal-800",
  "Delivered":                           "bg-emerald-50 text-emerald-800",
};

function fmtQty(n) {
  const v = Number(n || 0);
  return Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/\.?0+$/, "");
}

function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString();
  } catch {
    return "—";
  }
}

function JobRow({ job }) {
  return (
    <tr className="border-t border-slate-100" data-testid={`order-status-job-${job.code}`}>
      <td className="py-1.5 px-3 mono text-xs text-slate-600">{job.code}</td>
      <td className="py-1.5 px-3">
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STAGE_TONE[job.current_stage] || "bg-slate-100 text-slate-700"}`}>
          {job.current_stage_label || job.current_stage}
        </span>
      </td>
      <td className="py-1.5 px-3 text-right mono text-xs">{fmtQty(job.current_qty)}</td>
      <td className="py-1.5 px-3 text-xs text-slate-600 capitalize">{job.status}{job.qc_status ? ` · ${job.qc_status.replace(/_/g, " ")}` : ""}</td>
      <td className="py-1.5 px-3 text-xs text-slate-500">{job.batch_no || "—"}</td>
    </tr>
  );
}

function WorkOrderBlock({ wo }) {
  return (
    <div className="border border-slate-200 rounded-md overflow-hidden" data-testid={`order-status-wo-${wo.code}`}>
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2">
        <div className="flex items-center gap-2 text-sm">
          <Factory className="w-3.5 h-3.5 text-slate-500" />
          <span className="mono font-semibold text-slate-800">{wo.code}</span>
          <span className="text-xs text-slate-500">Qty {fmtQty(wo.qty)}</span>
        </div>
        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{wo.status}</span>
      </div>
      {wo.stage_summary?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-3 pt-2">
          {wo.stage_summary.map((s) => (
            <span key={s.stage} className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STAGE_TONE[s.stage] || "bg-slate-100 text-slate-700"}`}>
              {s.label}: {fmtQty(s.qty)}
            </span>
          ))}
        </div>
      )}
      {wo.jobs?.length > 0 ? (
        <table className="w-full text-sm mt-2">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.12em] text-slate-500">
              <th className="py-1 px-3">Job</th>
              <th className="py-1 px-3">Stage</th>
              <th className="py-1 px-3 text-right">Qty</th>
              <th className="py-1 px-3">Status</th>
              <th className="py-1 px-3">Batch</th>
            </tr>
          </thead>
          <tbody>
            {wo.jobs.map((j) => <JobRow key={j.id} job={j} />)}
          </tbody>
        </table>
      ) : (
        <div className="px-3 py-3 text-xs text-slate-400">No production job issued against this Work Order yet.</div>
      )}
    </div>
  );
}

// ============================================================
// Order Status Tracker sheet — the exact 35-column layout imported from the
// sales/dispatch team's Excel tracker, rendered as one horizontal row per
// TO NO + PART NUMBER (mirroring the sheet itself) right under that line's
// card. Every header always shows, even when nothing has been imported for
// that line yet — Delivery Date and Production Status are editable in every
// row and save (upserting a manual-only row if none exists yet) via PATCH,
// independent of any future re-import; every other column is read-only.
// ============================================================
const PRODUCTION_STATUS_OPTIONS = ["Work Order Raised", "In Production", "Ready for Dispatch", "Completed"];

// [field, header] in the tracker sheet's own column order — used verbatim
// as the table header row so it reads as the same sheet, left to right.
const TRACKER_COLUMNS = [
  ["month", "MONTH"], ["date", "DATE"], ["to_no", "TO NO"], ["marketing", "MARKETING"],
  ["customer_name", "CUSTOMER NAME"], ["description", "DISCRIPTION"], ["part_number", "PART NUMBER"],
  ["ord_qty", "ORD. QTY"], ["unit", "UNIT"], ["price", "PRICE"], ["discount_pct", "% Discount"],
  ["final_price", "Final Price"], ["delivery_date", "DELIVERY DATE"], ["revised_date", "REVISED DATE"],
  ["final_delivery", "FINAL DELIVERY"], ["revised_delivery_final_date", "REVISED DELIVERY FINAL DATE"],
  ["sales_order_number", "SALES ORDER NUMBER"], ["packed_qty", "PACKED QTY"], ["packed_qty_value", "PACKED QTY VALUE"],
  ["prd_comp_date", "PRD.COMP.DATE"], ["production_status", "PRODUCTION STATUS"], ["bl", "B/L"],
  ["balance_material_value", "BALANCE MATERIAL VALUE"], ["packed_status", "Against Order Material Packed Staus"],
  ["packed_timeliness", "PACKED ON TIME OR PACKED LATE OR PACKING PENDING"],
  ["pending_line_items", "PENDING LINE ITEMS WRT TRANSFER ORDER"], ["ready_before_after", "READY BEFORE OR AFTER"],
  ["dc_date", "D.C DATE"], ["dc_no", "DC NO"], ["dc", "DC"], ["invoice_number", "INVOICE NUMBER"],
  ["dispatch_date", "DISPATCH DATE"], ["dispatch_status", "DISPATCH STATUS"], ["payment_terms", "PAYMENT TERMS"],
  ["delivery_through", "DELIVERY THROUGH"],
];

const TRACKER_DATE_FIELDS = new Set(["date", "revised_date", "final_delivery", "revised_delivery_final_date", "prd_comp_date", "dc_date", "dispatch_date"]);
const TRACKER_NUM_FIELDS = new Set(["ord_qty", "price", "discount_pct", "final_price", "packed_qty", "packed_qty_value", "balance_material_value"]);

function fmtCellDate(d) {
  if (!d) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
  if (!m) return d; // raw, unparseable sheet text — show as-is rather than hide it
  try { return new Date(d).toLocaleDateString(); } catch { return d; }
}

function fmtTrackerVal(field, v) {
  if (v === null || v === undefined || v === "") return "—";
  if (TRACKER_DATE_FIELDS.has(field)) return fmtCellDate(v);
  if (TRACKER_NUM_FIELDS.has(field)) {
    const n = Number(v);
    if (Number.isNaN(n)) return String(v);
    return Number.isInteger(n) ? String(n) : n.toFixed(2);
  }
  return String(v);
}

// Matches backend order_status_tracker.py's _norm()/_key() — TO NO / PART
// NUMBER are identifier-like tokens, so ALL whitespace is stripped (not just
// collapsed) on top of case-folding before joining sheet rows to search
// results.
function trackerNorm(s) {
  return (s || "").replace(/\s+/g, "").toUpperCase();
}
function trackerKey(toNo, partNumber) {
  return `${trackerNorm(toNo)}::${trackerNorm(partNumber)}`;
}

// One horizontal row with all 35 sheet columns for one TO NO + PART NUMBER —
// `line` is the imported tracker row if one exists, or null/undefined if
// nothing's been imported for it yet (the row still renders, with "—" cells
// and working Delivery Date / Production Status editors).
function TrackerTable({ toNo, partNumber, line, onSaved, visibleFields }) {
  const row = line || {};
  const columns = visibleFields ? TRACKER_COLUMNS.filter(([f]) => visibleFields.has(f)) : TRACKER_COLUMNS;
  const [deliveryDate, setDeliveryDate] = useState(row.delivery_date || "");
  const [productionStatus, setProductionStatus] = useState(row.production_status || "");
  const [savingDate, setSavingDate] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);

  useEffect(() => { setDeliveryDate(row.delivery_date || ""); }, [row.delivery_date]);
  useEffect(() => { setProductionStatus(row.production_status || ""); }, [row.production_status]);

  const saveField = async (field, value, setSaving) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/order-status-tracker/line", {
        to_no: toNo, part_number: partNumber, [field]: value,
      });
      onSaved?.(data);
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-t border-slate-100 pt-3" data-testid={`tracker-table-${toNo}-${partNumber}`}>
      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-1.5 flex items-center gap-1.5">
        <FileSpreadsheet className="w-3 h-3" /> Order Status Tracker Sheet
        {!line && (
          <span className="normal-case tracking-normal text-slate-400 font-normal">
            — not imported for this line yet; Delivery Date / Production Status can still be set below.
          </span>
        )}
      </div>
      <div className="overflow-x-auto border border-slate-200 rounded-md">
        <table className="text-xs border-collapse w-max min-w-full">
          <thead>
            <tr className="bg-slate-100">
              {columns.map(([field, header]) => (
                <th
                  key={field}
                  className="px-2 py-1.5 text-left font-semibold text-slate-600 uppercase tracking-wide text-[9px] border border-slate-200 whitespace-nowrap"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {columns.map(([field]) => {
                if (field === "delivery_date") {
                  return (
                    <td key={field} className="border border-slate-200 p-1">
                      <div className="flex items-center gap-1">
                        <Input
                          type="date"
                          value={deliveryDate}
                          onChange={(e) => setDeliveryDate(e.target.value)}
                          onBlur={() => { if (deliveryDate !== (row.delivery_date || "")) saveField("delivery_date", deliveryDate, setSavingDate); }}
                          className="h-7 text-xs w-32"
                          data-testid={`tracker-delivery-date-${toNo}-${partNumber}`}
                        />
                        {savingDate && <Loader2 className="w-3 h-3 animate-spin text-slate-400 shrink-0" />}
                      </div>
                    </td>
                  );
                }
                if (field === "production_status") {
                  return (
                    <td key={field} className="border border-slate-200 p-1">
                      <div className="flex items-center gap-1">
                        <Select
                          value={productionStatus || "__blank"}
                          onValueChange={(v) => {
                            const val = v === "__blank" ? "" : v;
                            setProductionStatus(val);
                            saveField("production_status", val, setSavingStatus);
                          }}
                        >
                          <SelectTrigger className="h-7 text-xs w-36" data-testid={`tracker-production-status-${toNo}-${partNumber}`}>
                            <SelectValue placeholder="Not set" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__blank">Not set</SelectItem>
                            {PRODUCTION_STATUS_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                            {productionStatus && !PRODUCTION_STATUS_OPTIONS.includes(productionStatus) && (
                              <SelectItem value={productionStatus}>{productionStatus}</SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                        {savingStatus && <Loader2 className="w-3 h-3 animate-spin text-slate-400 shrink-0" />}
                      </div>
                    </td>
                  );
                }
                // Read-only cell. TO NO / PART NUMBER are known even before
                // any import — show them either way rather than "—".
                let val = row[field];
                if (field === "to_no" && !line) val = toNo;
                if (field === "part_number" && !line) val = partNumber;
                return (
                  <td key={field} className="border border-slate-200 px-2 py-1.5 mono text-slate-700 whitespace-nowrap">
                    {fmtTrackerVal(field, val)}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Standalone card for a TO NO that exists in the imported tracker sheet but
// has no matching Sales Order in the ERP yet (so there's no Work Order/
// Production Job info to show alongside it).
function TrackerOnlyCard({ line, onSaved, visibleFields }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3" data-testid={`tracker-only-${line.to_no}-${line.part_number}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Transfer Order</div>
          <div className="text-sm font-semibold text-slate-800 mono">{line.to_no}</div>
          <div className="text-xs text-slate-500">{line.customer_name || "—"}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Part Number</div>
          <div className="text-sm font-semibold text-slate-800 mono">{line.part_number}</div>
          <div className="text-xs text-slate-500">{line.description || "—"}</div>
        </div>
      </div>
      <TrackerTable toNo={line.to_no} partNumber={line.part_number} line={line} onSaved={onSaved} visibleFields={visibleFields} />
    </div>
  );
}

function ResultCard({ r, trackerLine, onTrackerSaved, visibleFields, canUnpack, onUnpack, unpackingId }) {
  const ps = r.packing_slip;
  const canUnpackThis = canUnpack && ps && ps.status === "packed" && ps.packed_qty > 0;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3" data-testid={`order-status-result-${r.transfer_order.code}-${r.item.code}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Transfer Order</div>
          <div className="text-sm font-semibold text-slate-800 mono">{r.transfer_order.display_code}</div>
          <div className="text-xs text-slate-500">{r.transfer_order.customer_name || "—"}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">FG Part No.</div>
          <div className="text-sm font-semibold text-slate-800 mono">{r.item.code}</div>
          <div className="text-xs text-slate-500">{r.item.name}</div>
        </div>
        <span className={`text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full font-medium ${OVERALL_TONE[r.overall_status] || "bg-slate-100 text-slate-700"}`} data-testid="order-status-badge">
          {r.overall_status}
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <div className="bg-slate-50 rounded-md p-2.5">
          <div className="text-slate-500 uppercase tracking-wide text-[10px]">Ordered</div>
          <div className="mono font-semibold text-slate-800 text-sm">{fmtQty(r.ordered_qty)} {r.item.uom}</div>
        </div>
        <div className="bg-slate-50 rounded-md p-2.5">
          <div className="text-slate-500 uppercase tracking-wide text-[10px]">Delivered</div>
          <div className="mono font-semibold text-emerald-700 text-sm">{fmtQty(r.delivered_qty)} {r.item.uom}</div>
        </div>
        <div className="bg-slate-50 rounded-md p-2.5">
          <div className="text-slate-500 uppercase tracking-wide text-[10px]">Pending Dispatch</div>
          <div className="mono font-semibold text-amber-700 text-sm">{fmtQty(r.pending_delivery_qty)} {r.item.uom}</div>
        </div>
        <div className="bg-slate-50 rounded-md p-2.5">
          <div className="text-slate-500 uppercase tracking-wide text-[10px]">FG On Hand</div>
          <div className="mono font-semibold text-blue-700 text-sm">{fmtQty(r.fg_on_hand_qty)} {r.item.uom}</div>
        </div>
      </div>

      {r.delivery_date && (
        <div className="text-xs text-slate-500 flex items-center gap-1.5">
          <Truck className="w-3.5 h-3.5" /> Delivery date: {fmtDate(r.delivery_date)}
        </div>
      )}

      {ps && (
        <div className="flex flex-wrap items-center justify-between gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs" data-testid={`order-status-ps-${r.transfer_order.code}-${r.item.code}`}>
          <div className="text-amber-800">
            Packing Slip <span className="mono font-semibold">{ps.code}</span> — {fmtQty(ps.packed_qty)} {r.item.uom} packed
            {ps.status !== "packed" && <span className="capitalize"> · {ps.status}</span>}
          </div>
          {canUnpackThis && (
            <Button
              variant="outline" size="sm" className="h-7"
              onClick={() => onUnpack(ps, r)}
              disabled={unpackingId === ps.id}
              title="Reverts this Packing Slip's packed quantity to 0, freeing it back into available Finished Goods — picked up immediately here and on the next MRP Run."
              data-testid={`order-status-unpack-${r.transfer_order.code}-${r.item.code}`}
            >
              {unpackingId === ps.id ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <PackageMinus className="w-3.5 h-3.5 mr-1.5" />}
              Unpack
            </Button>
          )}
        </div>
      )}

      <div className="space-y-2">
        {r.work_orders.length === 0 ? (
          <div className="text-xs text-slate-400 flex items-center gap-1.5">
            <Boxes className="w-3.5 h-3.5" /> No Work Order raised yet for this line.
          </div>
        ) : (
          r.work_orders.map((wo) => <WorkOrderBlock key={wo.id} wo={wo} />)
        )}
      </div>

      <TrackerTable
        toNo={r.transfer_order.display_code}
        partNumber={r.item.code}
        line={trackerLine}
        onSaved={onTrackerSaved}
        visibleFields={visibleFields}
      />
    </div>
  );
}

const DROPDOWN_LIMIT = 8;

function ImportBar({ onImported }) {
  const [importing, setImporting] = useState(false);

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/order-status-tracker/template", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }));
      const a = document.createElement("a");
      a.href = url; a.download = "order_status_template.xlsx";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setImporting(true);
    try {
      const { data } = await api.post("/order-status-tracker/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.warning) {
        // Header matched but nothing was imported — usually the blank
        // Template got uploaded instead of a filled-in sheet. Don't dress
        // this up as a plain success, or it looks like the import "worked"
        // with no explanation for why nothing shows up afterward.
        toast.warning(data.warning, { description: file.name });
      } else {
        toast.success(`Imported ${file.name} · created ${data.created}, updated ${data.updated}`);
      }
      onImported?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="order-status-tracker-admin-bar">
      <FileSpreadsheet className="w-4 h-4 text-slate-500 shrink-0" />
      <span className="text-xs text-slate-600 mr-auto">
        Bulk import the Order Status tracker sheet — 35-column .xlsx, upserted by TO NO + Part Number.
        Delivery Date and Production Status you've set here are never overwritten by a re-import.
      </span>
      <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="tracker-template-btn">
        <Download className="w-3.5 h-3.5 mr-1.5" /> Template
      </Button>
      <label
        className={`inline-flex items-center px-3 h-8 rounded-md border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50 cursor-pointer ${importing ? "opacity-60 pointer-events-none" : ""}`}
        data-testid="tracker-import-label"
      >
        {importing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
        {importing ? "Importing…" : "Import Sheet"}
        <input
          type="file"
          accept=".xlsx,.xlsm"
          className="hidden"
          onChange={onImport}
          disabled={importing}
          data-testid="tracker-import-input"
        />
      </label>
    </div>
  );
}

// ============================================================
// Module filter — "search by" Transfer Order / Sales Order / Delivery
// Challan / Packing Slip. Any of the four resolves (via
// GET /order-status-tracker/resolve) back to its originating Transfer
// Order number, which then drives the exact same search/render pipeline —
// there's only ever one kind of result view, just four doors into it.
// ============================================================
const DOC_TYPES = [
  { key: "transfer_order", label: "Transfer Orders", placeholder: "e.g. TO-3001 or ELB-90-25" },
  { key: "sales_order", label: "Sales Order", placeholder: "e.g. CSO-1001" },
  { key: "delivery_challan", label: "Delivery Challans", placeholder: "e.g. DC-2001" },
  { key: "packing_slip", label: "Packing Slips", placeholder: "e.g. PS-1001" },
];
const DOC_TYPE_ENDPOINT = {
  sales_order: "/customer-sales-orders",
  delivery_challan: "/delivery-challans",
  packing_slip: "/packing-slips",
};

function docCustomerName(docType, d, custMap) {
  if (docType === "delivery_challan") {
    return d.kind === "subcontract" ? (d.vendor_name || "—") : (custMap[d.customer_id]?.name || "—");
  }
  return d.customer_name || "—";
}

const ALL_TRACKER_FIELDS = TRACKER_COLUMNS.map(([f]) => f);

function ColumnsFilter({ visibleFields, setVisibleFields }) {
  const [open, setOpen] = useState(false);

  const toggle = (field) => {
    setVisibleFields((prev) => {
      const next = new Set(prev);
      if (next.has(field)) {
        if (next.size === 1) return prev; // keep at least one column visible
        next.delete(field);
      } else {
        next.add(field);
      }
      return next;
    });
  };

  return (
    <div className="relative">
      <Button variant="outline" size="sm" onClick={() => setOpen((o) => !o)} data-testid="tracker-columns-btn">
        <Columns3 className="w-3.5 h-3.5 mr-1.5" /> Columns ({visibleFields.size}/{ALL_TRACKER_FIELDS.length})
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div
            className="absolute z-20 right-0 mt-1 w-72 max-h-80 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg p-2"
            data-testid="tracker-columns-menu"
          >
            <div className="flex items-center justify-between px-1 pb-1.5 mb-1 border-b border-slate-100">
              <span className="text-[10px] uppercase tracking-wider text-slate-500">Visible Columns</span>
              <div className="flex gap-2">
                <button type="button" className="text-[10px] text-blue-600 hover:underline" onClick={() => setVisibleFields(new Set(ALL_TRACKER_FIELDS))}>
                  All
                </button>
                <button
                  type="button"
                  className="text-[10px] text-blue-600 hover:underline"
                  onClick={() => setVisibleFields(new Set(["delivery_date", "production_status"]))}
                >
                  Editable only
                </button>
              </div>
            </div>
            {TRACKER_COLUMNS.map(([field, header]) => (
              <label key={field} className="flex items-center gap-2 px-1 py-1 text-xs text-slate-700 hover:bg-slate-50 rounded cursor-pointer">
                <input
                  type="checkbox"
                  checked={visibleFields.has(field)}
                  onChange={() => toggle(field)}
                  className="shrink-0"
                />
                <span className="truncate">{header}</span>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function OrderStatus() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Matches POST /packing-slips/{id}/unpack's own role check.
  const canUnpack = roleAllowed(user?.role, ["admin", "planner", "operator", "production_manager", "stores"]);

  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [searched, setSearched] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [unpackingId, setUnpackingId] = useState(null);

  // Order Status tracker sheet data, merged into the search results above:
  // trackerMap holds one entry per TO NO + PART NUMBER (keyed to match
  // computed result lines); trackerOnlyLines holds the fallback case where
  // the query matched nothing in the ERP's own Sales Orders but the
  // imported tracker sheet still has rows for that TO NO.
  const [trackerMap, setTrackerMap] = useState({});
  const [trackerOnlyLines, setTrackerOnlyLines] = useState(null);

  // Which of the 35 columns show in every tracker table on this page.
  const [visibleFields, setVisibleFields] = useState(() => new Set(ALL_TRACKER_FIELDS));

  // "Search by" module filter — Transfer Order (default) / Sales Order /
  // Delivery Challan / Packing Slip. Only Transfer Order search uses the
  // existing `orders` (Pending Transfer Orders) dropdown; the other three
  // browse their own document list, then resolve back to a TO NO.
  const [docType, setDocType] = useState("transfer_order");
  const [docList, setDocList] = useState([]);
  const [docListLoading, setDocListLoading] = useState(false);
  const [custMap, setCustMap] = useState({});

  // Pending Transfer Orders — suggestions in the search box's dropdown.
  // Selecting one drills into that specific order's full status via the same
  // backend lookup a manual search uses.
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/sales-orders");
        setOrders(res.data || []);
      } catch {
        // The pending list is a convenience — direct search below still works
        // even if this fails, so don't block the page on it.
      } finally {
        setOrdersLoading(false);
      }
    })();
    // Only Delivery Challans need a separate customer lookup (Sales Orders /
    // Packing Slips already carry their own customer_name).
    api.get("/customers").then((res) => {
      setCustMap(Object.fromEntries((res.data || []).map((c) => [c.id, c])));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (docType === "transfer_order") return;
    let active = true;
    setDocListLoading(true);
    api.get(DOC_TYPE_ENDPOINT[docType])
      .then((res) => { if (active) setDocList(res.data || []); })
      .catch(() => { if (active) setDocList([]); })
      .finally(() => { if (active) setDocListLoading(false); });
    return () => { active = false; };
  }, [docType]);

  const filteredDocList = useMemo(() => {
    if (docType === "transfer_order") return [];
    const q = query.trim().toLowerCase();
    let list = (docList || []).slice().sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    if (q) {
      list = list.filter((d) =>
        (d.code || "").toLowerCase().includes(q) ||
        docCustomerName(docType, d, custMap).toLowerCase().includes(q)
      );
    }
    return list;
  }, [docList, query, docType, custMap]);

  const pendingOrders = useMemo(() => {
    return (orders || [])
      .map((o) => {
        const lines = o.lines || [];
        const pendingQty = lines.reduce(
          (sum, l) => sum + Math.max(0, Number(l.qty || 0) - Number(l.delivered_qty || 0)),
          0
        );
        return { ...o, displayCode: toDisplayCode(o.code), pendingQty, lineCount: lines.length };
      })
      .filter((o) => o.status !== "cancelled" && o.status !== "delivered" && o.pendingQty > 0)
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }, [orders]);

  const filteredPending = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return pendingOrders;
    return pendingOrders.filter((o) =>
      o.displayCode.toLowerCase().includes(q) ||
      (o.code || "").toLowerCase().includes(q) ||
      (o.customer_name || "").toLowerCase().includes(q) ||
      (o.po_reference || "").toLowerCase().includes(q)
    );
  }, [pendingOrders, query]);

  // Best-effort: pull in the imported tracker sheet's rows for every TO NO
  // this search matched, so each result card below can show its 35 columns
  // inline. A TO with nothing imported for it just gets no tracker section —
  // never blocks or errors the main (computed) results.
  const fetchTrackerForResults = async (results) => {
    const toNumbers = [...new Set((results || []).map((r) => r.transfer_order.display_code))];
    if (toNumbers.length === 0) {
      setTrackerMap({});
      return;
    }
    const settled = await Promise.allSettled(
      toNumbers.map((to) => api.get("/order-status-tracker/lines", { params: { to_no: to } }))
    );
    const map = {};
    settled.forEach((res) => {
      if (res.status === "fulfilled") {
        (res.value.data.lines || []).forEach((l) => {
          map[trackerKey(l.to_no, l.part_number)] = l;
        });
      }
    });
    setTrackerMap(map);
  };

  // Fallback for a query that matched nothing in the ERP's own Sales Orders
  // (404 from /order-status) — the tracker sheet may still carry this TO NO
  // (e.g. an older order that was never raised as a Sales Order here).
  // Returns true if it found something, so the caller can skip the "not
  // found" error.
  const tryTrackerOnly = async (qValue) => {
    try {
      const { data: trackerData } = await api.get("/order-status-tracker/lines", { params: { to_no: qValue } });
      setTrackerOnlyLines(trackerData.lines || []);
      return (trackerData.lines || []).length > 0;
    } catch {
      return false;
    }
  };

  const loadStatus = async (qValue) => {
    setDropdownOpen(false);
    setLoading(true);
    setSearched(true);
    setData(null);
    setErrorMsg(null);
    setTrackerMap({});
    setTrackerOnlyLines(null);
    try {
      const res = await api.get("/order-status", { params: { q: qValue } });
      setData(res.data);
      fetchTrackerForResults(res.data.results);
    } catch (e) {
      if (e?.response?.status === 404) {
        const foundTrackerOnly = await tryTrackerOnly(qValue);
        if (!foundTrackerOnly) {
          const msg = e.response.data?.detail || `No matching Transfer Order or FG part number found for "${qValue}".`;
          setErrorMsg(msg);
          toast.error(msg);
        }
      } else {
        const msg = formatApiError(e?.response?.data?.detail) || "Failed to fetch order status — check your connection and try again.";
        setErrorMsg(msg);
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  // Resolves a Sales Order / Delivery Challan / Packing Slip number back to
  // its originating Transfer Order, then hands off to the exact same
  // loadStatus() pipeline a direct TO search uses.
  const resolveAndLoad = async (code) => {
    setDropdownOpen(false);
    setSearched(true);
    setLoading(true);
    setErrorMsg(null);
    try {
      const { data: resolved } = await api.get("/order-status-tracker/resolve", { params: { doc_type: docType, q: code } });
      await loadStatus(resolved.to_no);
    } catch (e) {
      setLoading(false);
      const msg = e?.response?.status === 404
        ? (e.response.data?.detail || `No matching document found for "${code}".`)
        : (formatApiError(e?.response?.data?.detail) || "Failed to resolve document.");
      setErrorMsg(msg);
      toast.error(msg);
    }
  };

  const runSearch = () => {
    const q = query.trim();
    if (!q) {
      toast.error(`Enter a ${DOC_TYPES.find((d) => d.key === docType)?.label.replace(/s$/, "") || "document"} number`);
      return;
    }
    if (docType === "transfer_order") { loadStatus(q); return; }
    resolveAndLoad(q);
  };

  const selectOrder = (o) => {
    setQuery(o.displayCode);
    loadStatus(o.code);
  };

  const selectDoc = (d) => {
    setQuery(d.code);
    resolveAndLoad(d.code);
  };

  const backToList = () => {
    setSearched(false);
    setData(null);
    setErrorMsg(null);
    setQuery("");
    setTrackerMap({});
    setTrackerOnlyLines(null);
  };

  const onTrackerSaved = (updated) => {
    setTrackerMap((m) => ({ ...m, [trackerKey(updated.to_no, updated.part_number)]: updated }));
    setTrackerOnlyLines((prev) => prev && prev.map((l) => (
      trackerKey(l.to_no, l.part_number) === trackerKey(updated.to_no, updated.part_number) ? { ...l, ...updated } : l
    )));
  };

  const onImported = () => {
    // Re-pull tracker data for whatever's currently on screen so a fresh
    // import shows up immediately without a second manual search.
    if (data?.results?.length) fetchTrackerForResults(data.results);
    else if (searched && query.trim()) tryTrackerOnly(query.trim());
  };

  // Reverts a Packing Slip's packed qty to 0 (POST /packing-slips/{id}/unpack)
  // right from Order Status — frees that quantity back into available
  // Finished Goods (fg_on_hand_qty / the tracker's PACKED QTY column and the
  // next MRP Run all read live FG_STORE stock, so this shows up there too
  // without any extra step).
  const unpackLine = async (ps, r) => {
    setUnpackingId(ps.id);
    try {
      await api.post(`/packing-slips/${ps.id}/unpack`);
      toast.success(`${ps.code} unpacked — ${fmtQty(ps.packed_qty)} ${r.item.uom} freed back to Finished Goods`);
      await loadStatus(data?.query || query);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Unpack failed");
    } finally {
      setUnpackingId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="order-status-page">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Sales</div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
          Order Status
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Search by Transfer Order, Sales Order, Delivery Challan, or Packing Slip — every line shows live data
          from that order's own module, with Delivery Date and Production Status editable in place.
        </p>
      </div>

      {isAdmin && <ImportBar onImported={onImported} />}

      <div className="flex items-center gap-1.5 flex-wrap" data-testid="order-status-doc-type-filter">
        <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-1">Search by</span>
        {DOC_TYPES.map((dt) => (
          <button
            key={dt.key}
            type="button"
            // Same fix as the dropdown options below: without this, clicking
            // a pill while the search input is focused blurs it, and the
            // input's own onBlur timer closes the dropdown ~150ms after this
            // click just opened it.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { setDocType(dt.key); setQuery(""); setDropdownOpen(true); }}
            className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
              docType === dt.key
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
            }`}
            data-testid={`doc-type-${dt.key}`}
          >
            {dt.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {searched && (
          <Button variant="ghost" size="sm" onClick={backToList} data-testid="order-status-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        )}
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <Input
            placeholder={DOC_TYPES.find((d) => d.key === docType)?.placeholder}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setDropdownOpen(true); }}
            onFocus={() => setDropdownOpen(true)}
            onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
            onKeyDown={(e) => {
              if (e.key === "Enter") runSearch();
              if (e.key === "Escape") setDropdownOpen(false);
            }}
            className="pl-9"
            data-testid="order-status-search-input"
          />

          {dropdownOpen && docType === "transfer_order" && (
            <div
              className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden"
              data-testid="order-status-dropdown"
            >
              <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-100 bg-slate-50">
                <span className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                  <ClipboardList className="w-3 h-3" /> Pending Transfer Orders
                </span>
                <span className="text-[10px] text-slate-400 mono">{filteredPending.length}</span>
              </div>
              <div className="max-h-72 overflow-y-auto">
                {ordersLoading ? (
                  <div className="px-3 py-4 text-center text-xs text-slate-500">Loading…</div>
                ) : filteredPending.length === 0 ? (
                  <div className="px-3 py-4 text-center text-xs text-slate-500">
                    No pending Transfer Orders match — press Search for an FG part number or an already-delivered order.
                  </div>
                ) : (
                  filteredPending.slice(0, DROPDOWN_LIMIT).map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      // The input's onBlur (fired the instant this button takes
                      // focus) queues the dropdown closed on a timer — racing
                      // that against onClick is what made a click sometimes do
                      // nothing. Blocking the mousedown-triggered blur outright
                      // keeps the input focused so onClick always fires.
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => selectOrder(o)}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                      data-testid={`pending-to-option-${o.code}`}
                    >
                      <span className="min-w-0">
                        <span className="mono font-semibold text-sm text-slate-800">{o.displayCode}</span>
                        <span className="block text-xs text-slate-500 truncate">{o.customer_name || "—"}</span>
                      </span>
                      <span className="text-right shrink-0">
                        <span className={`block text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${TO_STATUS_TONE[o.status] || "bg-slate-100 text-slate-700"}`}>
                          {TO_STATUS_LABEL[o.status] || o.status}
                        </span>
                        <span className="block text-xs mono font-semibold text-amber-700 mt-0.5">{fmtQty(o.pendingQty)} pending</span>
                      </span>
                    </button>
                  ))
                )}
                {filteredPending.length > DROPDOWN_LIMIT && (
                  <div className="px-3 py-1.5 text-[10px] text-slate-400 text-center border-t border-slate-100">
                    +{filteredPending.length - DROPDOWN_LIMIT} more — keep typing to narrow down
                  </div>
                )}
              </div>
            </div>
          )}

          {dropdownOpen && docType !== "transfer_order" && (
            <div
              className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden"
              data-testid="order-status-doc-dropdown"
            >
              <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-100 bg-slate-50">
                <span className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                  <ClipboardList className="w-3 h-3" /> Recent {DOC_TYPES.find((d) => d.key === docType)?.label}
                </span>
                <span className="text-[10px] text-slate-400 mono">{filteredDocList.length}</span>
              </div>
              <div className="max-h-72 overflow-y-auto">
                {docListLoading ? (
                  <div className="px-3 py-4 text-center text-xs text-slate-500">Loading…</div>
                ) : filteredDocList.length === 0 ? (
                  <div className="px-3 py-4 text-center text-xs text-slate-500">No matches — keep typing and press Search.</div>
                ) : (
                  filteredDocList.slice(0, DROPDOWN_LIMIT).map((d) => (
                    <button
                      key={d.id || d.code}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => selectDoc(d)}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                      data-testid={`doc-option-${d.code}`}
                    >
                      <span className="min-w-0">
                        <span className="mono font-semibold text-sm text-slate-800">{d.code}</span>
                        <span className="block text-xs text-slate-500 truncate">{docCustomerName(docType, d, custMap)}</span>
                      </span>
                      {d.status && (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700 shrink-0">
                          {d.status}
                        </span>
                      )}
                    </button>
                  ))
                )}
                {filteredDocList.length > DROPDOWN_LIMIT && (
                  <div className="px-3 py-1.5 text-[10px] text-slate-400 text-center border-t border-slate-100">
                    +{filteredDocList.length - DROPDOWN_LIMIT} more — keep typing to narrow down
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <Button onClick={runSearch} disabled={loading} data-testid="order-status-search-btn">
          {loading ? "Searching…" : "Search"}
        </Button>
        <ColumnsFilter visibleFields={visibleFields} setVisibleFields={setVisibleFields} />
      </div>

      {!searched && !dropdownOpen && (
        <div className="bg-white border border-dashed border-slate-200 rounded-lg p-10 text-center text-sm text-slate-500">
          <PackageSearch className="w-8 h-8 mx-auto mb-2 text-slate-300" />
          {docType === "transfer_order"
            ? "Click the search box above to browse pending Transfer Orders, or type a Transfer Order number (e.g. TO-3001) or an FG part number (e.g. ELB-90-25) and press Search."
            : `Click the search box above to browse recent ${DOC_TYPES.find((d) => d.key === docType)?.label}, or type a number and press Search.`}
        </div>
      )}

      {searched && loading && (
        <div className="bg-white border border-slate-200 rounded-lg p-10 text-center text-sm text-slate-500" data-testid="order-status-loading">
          Loading order status for <span className="mono font-semibold">{query}</span>…
        </div>
      )}

      {searched && !loading && errorMsg && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-6 text-sm text-rose-800" data-testid="order-status-error">
          {errorMsg}
        </div>
      )}

      {searched && !loading && !errorMsg && data && data.results.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-8 text-center text-sm text-slate-500" data-testid="order-status-empty">
          {data.match_type === "fg_item"
            ? `"${data.matched_item?.code}" — ${data.matched_item?.name} exists, but isn't on any Transfer Order yet.`
            : "No lines found on this order."}
        </div>
      )}

      {data && data.results.length > 0 && (
        <div className="space-y-4">
          {data.match_type === "fg_item" && (
            <div className="text-xs text-slate-500">
              Showing every Transfer Order line for FG part <span className="mono font-semibold">{data.matched_item?.code}</span> — {data.matched_item?.name}
            </div>
          )}
          {data.results.map((r) => (
            <ResultCard
              key={`${r.transfer_order.id}-${r.item.id}`}
              r={r}
              trackerLine={trackerMap[trackerKey(r.transfer_order.display_code, r.item.code)]}
              onTrackerSaved={onTrackerSaved}
              visibleFields={visibleFields}
              canUnpack={canUnpack}
              onUnpack={unpackLine}
              unpackingId={unpackingId}
            />
          ))}
        </div>
      )}

      {searched && !loading && !errorMsg && !data && trackerOnlyLines && trackerOnlyLines.length > 0 && (
        <div className="space-y-4">
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            "{query}" isn't a Sales Order in this ERP yet — showing data from the imported Order Status tracker sheet only.
          </div>
          {trackerOnlyLines.map((l) => (
            <TrackerOnlyCard key={`${l.to_no}-${l.part_number}`} line={l} onSaved={onTrackerSaved} visibleFields={visibleFields} />
          ))}
        </div>
      )}
    </div>
  );
}
